import _                                                      from 'underscore';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { DropdownProps, Icon, Input, Menu, Popup, Segment }   from 'semantic-ui-react';
import { useLazyQuery, useMutation, useQuery }                from '@apollo/react-hooks';
import { Cell, Grid }                                         from 'styled-css-grid';

import { CorvalAccount, CorvalAccountLike, CorvalOrganization, CorvalProjectActivity, CorvalStaticProjectActivity, CorvalUIColor, GQLNode, Position, SemanticUIColor, Size, Uuid } from '../../../types';

import { DecisionEngineQueries, SystemAccountingQueries } from '../../../queries';
import { useCurrentUser, useSystemToasts }                from '../../../hooks';
import { queriesHaveData, queriesLoading }                from '../../../store/_apolloUtil';
import { EMPTY_VALUE_DISPLAY }                            from '../../../constants/FormatStringEnums';
import { UserService }                                    from '../../../services';
import { StyledContainer }                                from '../../containers';
import { ProjectContext }                                 from '../../pages/projects/ProjectIndexPage';
import { UserLink }                                       from '../../pages/users/UserLink';
import { ItemFromQueryProps }                             from '../from-query/ItemFromQuery';
import { UserAvatar }                                     from '../user-avatar/UserAvatar';
import { InlineLoadingIndicatorLabel }                    from '../util/loading/InlineLoadingIndicatorLabel';
import { GravatarProps }                                  from '../util/visual/Gravatar';
import { FinePrintMessage }                               from '../util/messaging/FinePrintMessage';
import { ENTITY_ICONS }                                   from '../../../constants/ProductEnums';
import { ProjectQueries }                                 from "@src/queries/ProjectQueries";


export type EntityWatcherType = 'projectActivity' | 'staticProjectActivity'

interface Props {
    entityId: Uuid,
    entityType: EntityWatcherType
    editable?: boolean,
    projectId?: Uuid,
    organizationId?: Uuid,
    projectVersionId?: Uuid,
    containerComponent?: FC,
    menuDirection?: DropdownProps['direction'],
    itemFlowDirection?: 'start' | 'end' | 'evenly' | 'around' | 'between',
    avatarSize?: GravatarProps['size'],
    searchInputMinItemCount?: number,
    sortBy?: 'firstName' | 'lastName'
}

const defaultProps: Props = {
    entityId:                null,
    entityType:              null,
    editable:                true,
    projectId:               null,
    organizationId:          null,
    avatarSize:              30,
    containerComponent:      ( props ) => <Segment size={ 'small' } secondary basic={ true } style={ { padding: `10px 5px`, borderRadius: 4 } }>{ props.children }</Segment>,
    menuDirection:           'left',
    itemFlowDirection:       'start',
    searchInputMinItemCount: 5,
    sortBy:                  'firstName',
}

type EntityWatcherHashInfoType = Record<EntityWatcherType, {
    entityName: ( entity: any ) => string,
    watchersRequestOptions: ItemFromQueryProps,
    memberRequestOptions: ItemFromQueryProps,
    mutationAdd: GQLNode,
    mutationRemove: GQLNode,
    mutationAddOptionsFunc: any,
    mutationRemoveOptionsFunc: any,
    memberShouldBeListedAsWatcher: ( member: CorvalAccount ) => boolean,
}>

type EntityUserObject = Omit<CorvalAccountLike, 'emailAddress'>

const EntityWatchers: FC<Props> = ( props ) => {

    let { projectId }                             = _.isString( props.projectId ) ? props : useContext( ProjectContext ),
        { organizationId }                        = _.isString( props.organizationId ) ? props : useContext( ProjectContext ),
        { projectVersionId }                      = _.isString( props.projectVersionId ) ? props : useContext( ProjectContext ),
        { currentUser }                           = useCurrentUser(),
        toastActions                              = useSystemToasts(),
        [ entity, setEntity ]                     = useState( null ),
        [ watcherList, setWatcherList ]           = useState<EntityUserObject[]>( [] ),
        [ memberList, setMemberList ]             = useState<EntityUserObject[]>( [] ),
        [ activeMemberList, setActiveMemberList ] = useState<EntityUserObject[]>( [] ),
        [ searchTerm, setSearchTerm ]             = useState<string>( '' ),
        searchInputRef                            = useRef( null ),
        showSearch                                = ( !_.isEmpty( memberList ) && ( memberList.length >= props.searchInputMinItemCount ) ),
        searchEmpty                               = ( _.isEmpty( searchTerm ) );

    let operationTypeHash: EntityWatcherHashInfoType = {

        'projectActivity': {

            entityName: ( entity: CorvalProjectActivity ) => `"${ entity.displayName }"`,

            watchersRequestOptions: {
                query:             DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `id displayName watchers{ account { id firstName lastName } }` ),
                queryOptions:      {
                    variables:   {
                        projectId,
                        includeGeneratedRecurrences: true,
                        where:                       { id: { eq: props.entityId } }
                    },
                    fetchPolicy: 'network-only',
                },
                queryResultObject: ( data: { projectActivites: CorvalProjectActivity[] } ) => {

                    return _.first( data.projectActivites ) ?? {};
                },
            },

            memberRequestOptions: {
                query:             SystemAccountingQueries.queries.GET_ORGANIZATION( `
									organizationMembers{
										isActive
										accountId
										organizationRole
										account{
											id
											emailAddress
											firstName
											lastName
											businessFunctions
											systemRole
											projects{
												projectId
												projectRole
											}
										}
									}`
                ),
                queryOptions:      {
                    variables: { id: organizationId },
                },
                queryResultObject: ( data: { organizations: CorvalOrganization[] } ) => {

                    return _.chain( data.organizations[ 0 ].organizationMembers )
                            .pluck( 'account' )
                            .filter( operationTypeHash[ props.entityType ].memberShouldBeListedAsWatcher )
                            .sortBy( props.sortBy )
                            .value();
                },
            },

            mutationAdd: DecisionEngineQueries.mutations.ADD_WATCHER_TO_PROJECT_ACTIVITY(),

            mutationAddOptionsFunc: ( account: CorvalAccountLike ) => {

                return ( {
                    variables: {
                        input: {
                            accountId:         account.id,
                            projectActivityId: entity.id,
                        }
                    }
                } );
            },

            mutationRemove: DecisionEngineQueries.mutations.REMOVE_WATCHER_FROM_PROJECT_ACTIVITY(),

            mutationRemoveOptionsFunc: ( account: CorvalAccountLike ) => {
                return ( {
                    variables: {
                        input: {
                            accountId:         account.id,
                            projectActivityId: entity.id,
                        }
                    }
                } );
            },

            memberShouldBeListedAsWatcher: ( member: CorvalAccount ) => {

                let isProjectMember    = _.pluck( member.projects, 'projectId' ).includes( projectId ),
                    isNotInWatcherList = !_.pluck( watcherList, 'id' ).includes( member.id );

                return ( isProjectMember && isNotInWatcherList );
            }
        },

        'staticProjectActivity': {

            entityName: ( entity: CorvalStaticProjectActivity ) => `"${ entity.name }"`,

            watchersRequestOptions: {
                query:             DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `id name watchers{ account { id firstName lastName } }`, projectVersionId ),
                queryOptions:      {
                    variables:   {
                        projectId,
                        projectVersionId,
                        where: { id: { eq: props.entityId } }
                    },
                    fetchPolicy: 'network-only',
                },
                queryResultObject: ( data: { staticProjectActivites: CorvalStaticProjectActivity[] } ) => {

                    return _.first( data.staticProjectActivites ) ?? {};
                },
            },

            memberRequestOptions: {
                query:             SystemAccountingQueries.queries.GET_ORGANIZATION( `
									organizationMembers{
										isActive
										accountId
										organizationRole
										account{
											id
											emailAddress
											firstName
											lastName
											businessFunctions
											systemRole
											projects{
												projectId
												projectRole
											}
										}
									}`
                ),
                queryOptions:      {
                    variables: { id: organizationId },
                },
                queryResultObject: ( data: { organizations: CorvalOrganization[] } ) => {

                    return _.chain( data.organizations[ 0 ].organizationMembers )
                            .pluck( 'account' )
                            .filter( operationTypeHash[ props.entityType ].memberShouldBeListedAsWatcher )
                            .sortBy( props.sortBy )
                            .value();
                },
            },

            mutationAdd: ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA(),

            mutationAddOptionsFunc: ( account: CorvalAccountLike ) => {

                const watchers = _.chain( entity?.watchers ?? [] )
                                  .pluck('account')
                                  .pluck('id')
                                  .uniq()
                                  .value();

                return ( {
                    variables: {
                        input: {
                            projectVersionId: projectVersionId,
                            activityChanges:  {
                                modified: [ {
                                    id:                entity.id,
                                    watcherAccountIds: [ ...watchers, account.id ]
                                } ]
                            }
                        }
                    }
                } );
            },

            mutationRemove: ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA(),

            mutationRemoveOptionsFunc: ( account: CorvalAccountLike ) => {

                const watchers = _.chain( entity?.watchers ?? [] )
                                  .pluck('account')
                                  .pluck('id')
                                  .uniq()
                                  .value();

                return ( {
                    variables: {
                        input: {
                            projectVersionId: projectVersionId,
                            activityChanges:  {
                                modified: [ {
                                    id:                entity.id,
                                    watcherAccountIds: _.without( watchers, account.id )
                                } ]
                            }
                        }
                    }
                } );
            },

            memberShouldBeListedAsWatcher: ( member: CorvalAccount ) => {

                let isProjectMember    = _.pluck( member.projects, 'projectId' ).includes( projectId ),
                    isNotInWatcherList = !_.pluck( watcherList, 'id' ).includes( member.id );

                return ( isProjectMember && isNotInWatcherList );
            }
        },
    }

    if( !props.entityId || !props.entityType ) return <>{ EMPTY_VALUE_DISPLAY }</>;

    let entityOperations                                 = operationTypeHash[ props.entityType ],
        { watchersRequestOptions, memberRequestOptions } = entityOperations,
        { mutationAdd, mutationAddOptionsFunc }          = entityOperations,
        { mutationRemove, mutationRemoveOptionsFunc }    = entityOperations,
        [ mutationAddFunc, mutationAddFuncReq ]          = useMutation( mutationAdd ),
        [ mutationRemoveFunc, mutationRemoveFuncReq ]    = useMutation( mutationRemove ),
        [ memberOperations, memberOperationReq ]         = useLazyQuery( memberRequestOptions.query ),
        watcherOperationReq                              = useQuery( watchersRequestOptions.query, watchersRequestOptions.queryOptions );

    let isLoadingWatchers = queriesLoading( watcherOperationReq ),
        isLoadingMembers  = queriesLoading( memberOperationReq );

    useEffect( () => {

        if( queriesHaveData( watcherOperationReq ) ){

            let entity      = watchersRequestOptions.queryResultObject( watcherOperationReq.data ),
                watcherList = ( entity?.watchers ?? [] ).map( watcher => ( watcher.account ) );

            setEntity( entity );

            setWatcherList( watcherList );

            memberOperations( memberRequestOptions.queryOptions )
        }

    }, [ watcherOperationReq ] );

    useEffect( () => {

        if( queriesHaveData( memberOperationReq ) ){

            let data = memberRequestOptions.queryResultObject( memberOperationReq.data ) ?? [];

            setMemberList( data );

            setActiveMemberList( data );
        }

    }, [ memberOperationReq ] );

    useEffect( () => {

        if( !_.isEmpty( searchTerm ) ){

            let filteredActiveMembers = _.filter( memberList, member => ( UserService.getUserName( member ).toLowerCase().search( searchTerm ) !== -1 ) );

            setActiveMemberList( filteredActiveMembers );

        } else {

            setActiveMemberList( memberList );
        }

    }, [ searchTerm ] )

    const handleSearchTermUpdated = ( e ) => {

        setSearchTerm( e.target.value );
    }

    const handleClearSearchTerm = () => {

        setSearchTerm( '' );
    }

    const updateUserToWatcherList = ( account: EntityUserObject ) => {

        setWatcherList( prevWatchers => _.sortBy( [ ...prevWatchers, account ], props.sortBy ) );

        setActiveMemberList( prevMembers => _.reject( prevMembers, { id: account.id } ) );

    }

    const updateUserToMemberList = ( account: EntityUserObject ) => {

        setWatcherList( prevWatchers => _.reject( prevWatchers, { id: account.id } ) );

        setActiveMemberList( prevMembers => _.sortBy( [ ...prevMembers, account ], props.sortBy ) );
    }

    const handleAddWatcher = async ( account: EntityUserObject ) => {

        try{

            updateUserToWatcherList( account );

            await mutationAddFunc( mutationAddOptionsFunc( account ) ).then( ( response ) => {

                if( response ){

                    toastActions.info( 'Follower Added!', `${ UserService.getUserName( account ) } is now a follower of ${ entityOperations.entityName( entity ) }`, { icon: { name: ENTITY_ICONS.USER.WATCHER } } );

                } else {

                    updateUserToMemberList( account );

                    toastActions.error( 'Mutation Error', mutationAddFuncReq.error.message, { timeoutMS: 0 } )
                }

            } );

        } catch( e ) {

            updateUserToMemberList( account );

            toastActions.error( 'Network Error', `${ e.message }` );
        }
    };

    const handleRemoveWatcher = async ( account: EntityUserObject ) => {

        try{

            updateUserToMemberList( account );

            await mutationRemoveFunc( mutationRemoveOptionsFunc( account ) ).then( ( response ) => {

                if( response ){

                    toastActions.info( 'Follower Removed!', `${ UserService.getUserName( account ) } was removed as a follower.`, { icon: { name: ENTITY_ICONS.USER.WATCHER } } );

                } else {

                    updateUserToWatcherList( account );

                    toastActions.error( 'Mutation Error', mutationRemoveFuncReq.error.message, { timeoutMS: 0 } )
                }

            } );

        } catch( e ) {

            updateUserToWatcherList( account );

            toastActions.error( 'Network Error', `${ e.message }` );
        }
    };

    const WatcherListItemContainer: FC = ( props ) => (

        <div className='peek-container position--relative margin-xs opacity--80 opacity-hover--100'>

            { props.children }

        </div>

    );

    const WatcherListItem: FC<{ user: EntityUserObject }> = ( { user } ) => (

        <WatcherListItemContainer>

            { props.editable && (
                <StyledContainer
                    bgColor={ CorvalUIColor.WHITE }
                    onClick={ () => handleRemoveWatcher( user ) }
                    className={ 'peek-item mask--circle cursor--pointer' }
                    position={ Position.ABSOLUTE }
                    top={ -5 } right={ -5 }>

                    <Icon name={ 'remove circle' } color={ 'red' } fitted={ true }/>

                </StyledContainer>
            ) }

            <UserLink organizationId={ organizationId } accountId={ user.id }>

                <UserAvatar user={ user } asInitials={ true } avatarSize={ props.avatarSize }/>

            </UserLink>

        </WatcherListItemContainer>
    );

    const WatcherListAddUserMenuItem: FC<{ loading?: boolean }> = ( { loading = false } ) => (

        <WatcherListItemContainer>

            <StyledContainer borderSize={ Size.SM }
                             borderColor={ SemanticUIColor.GREY }
                             className={ 'display--inline-grid flexbox--align-center flexbox--justify-center mask--circle cursor--pointer' }
                             style={ { width: props.avatarSize, height: props.avatarSize } }>

                <Icon loading={ loading } name={ loading ? 'circle notch' : 'plus' } color={ 'grey' } fitted/>

            </StyledContainer>

        </WatcherListItemContainer>
    )

    let openMenuFromRight = ( props.menuDirection === 'right' ),
        menuStyle         = {
            maxHeight: 200,
            minWidth:  showSearch ? 300 : null,
            width:     'max-content',
            overflowY: 'auto',
            overflowX: 'hidden'
        };

    return (

        <div className={ `flexbox flexbox--justify-${ props.itemFlowDirection } flexbox--align-center` }>

            { isLoadingWatchers && <InlineLoadingIndicatorLabel label={ 'Retrieving followers...' }/> }

            { !isLoadingWatchers && (

                <>

                    {/* WATCHER LIST - Flex list of assigned watchers for this entity type */ }
                    { watcherList.map( user => (

                        <WatcherListItem key={ user.id } user={ user }/>

                    ) ) }


                    { props.editable && <>

                        {/* MEMBER LIST - List of available members that can be watchers for this entity type */ }
                        { isLoadingMembers
                          ? <WatcherListAddUserMenuItem loading={ true }/>
                          : <Popup
                              popperDependencies={ [ searchTerm, memberList, activeMemberList ] }
                              className={ 'no-padding text-left' }
                              openOnTriggerMouseEnter={ false }
                              openOnTriggerClick={ true }
                              closeOnTriggerClick={ true }
                              closeOnTriggerMouseLeave={ false }
                              closeOnPortalMouseLeave={ false }
                              closeOnDocumentClick={ true }
                              closeOnEscape={ true }
                              position={ ( openMenuFromRight ) ? 'bottom right' : 'bottom left' }
                              trigger={ (

                                  <div>
                                      <WatcherListAddUserMenuItem/>
                                  </div>

                              ) }
                              onOpen={ ( event, data ) => {

                                  setTimeout( () => {

                                      searchInputRef.current?.focus();

                                  }, 0 );

                              } }
                              onClose={ ( event, data ) => {

                                  handleClearSearchTerm();

                              } }
                              content={ (
                                  <>
                                      {/* Generic Search Input */ }
                                      { showSearch && (

                                          <StyledContainer padding={ [ Size.SM, Size.XS, Size.XS, Size.XS ] } position={ Position.STICKY } top={ 0 } bgColor={ CorvalUIColor.WHITE }>

                                              { !searchEmpty && (
                                                  <StyledContainer position={ Position.ABSOLUTE }
                                                                   bgColor={ CorvalUIColor.WHITE }
                                                                   className={ 'mask--circle display--grid flexbox--justify-center flexbox--align-center cursor-pointer' }
                                                                   zIndex={ 200 }
                                                                   top={ 14 }
                                                                   right={ 12 }
                                                                   style={ { width: 30, height: 28, } }
                                                                   onClick={ handleClearSearchTerm }>

                                                      <Icon name={ 'remove' } color={ 'grey' }/>

                                                  </StyledContainer>
                                              ) }

                                              <StyledContainer position={ Position.RELATIVE } zIndex={ 100 }>

                                                  <Input ref={ searchInputRef }
                                                         fluid
                                                         clearable
                                                         value={ searchTerm }
                                                         type={ 'text' }
                                                         placeholder={ 'Search...' }
                                                         icon={ 'search' }
                                                         iconPosition={ 'left' }
                                                         onChange={ handleSearchTermUpdated }
                                                  />

                                              </StyledContainer>

                                          </StyledContainer>

                                      ) }

                                      {/* Actual User list */ }
                                      <Menu vertical borderless style={ menuStyle } className={ 'no-border no-box-shadow no-margin' }>

                                          { _.isEmpty( activeMemberList )
                                            ? (
                                                <FinePrintMessage className={ 'padding-sm' }>
                                                    <Icon color='grey' name='users'/> No users available.
                                                </FinePrintMessage>

                                            ) : (

                                                <>
                                                    { activeMemberList.map( user => (

                                                        <Menu.Item key={ user.id } onClick={ () => handleAddWatcher( user ) }>

                                                            <Grid columns={ `auto 1fr` } rows={ `auto` }>

                                                                <Cell middle><UserAvatar user={ user } asInitials={ true } avatarSize={ 20 } size={ 'micro' }/></Cell>

                                                                <Cell middle className={ 'text--sm' }>{ UserService.getUserName( user, ( props.sortBy === 'lastName' ) ) }</Cell>

                                                            </Grid>

                                                        </Menu.Item>
                                                    ) ) }
                                                </>
                                            ) }

                                      </Menu>
                                  </>
                              ) }
                          />
                        }

                    </> }

                </>

            ) }

        </div>
    );
};

EntityWatchers.defaultProps = defaultProps;

export { EntityWatchers }
